/**
 * R232q5az: Active modals stack — single source of truth for which UI
 * panels are currently open. Used to:
 *   1) auto-close the previously-open modal when a new one opens (M01),
 *   2) wire one Escape handler in World.tsx that pops the top of stack,
 *   3) avoid the previous "stack of 4 modals on top of each other" UX.
 *
 * Each toggle-overlay component (Inventory, Achievements, Auction, Bank,
 * Talents, etc.) MUST call:
 *   useEffect(() => {
 *     if (open) registerModal(MODAL_ID, () => setOpen(false));
 *     else unregisterModal(MODAL_ID);
 *     return () => unregisterModal(MODAL_ID);
 *   }, [open]);
 *
 * The store is intentionally NOT a "single open" state machine — multiple
 * stack entries are kept so Esc can close them in LIFO order.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

type CloseFn = () => void;

interface ModalEntry {
  id: string;
  close: CloseFn;
}

interface ActiveModalsStore {
  /** Ordered list, last entry = topmost (closes first on Esc). */
  stack: ModalEntry[];
  registerModal: (id: string, close: CloseFn) => void;
  unregisterModal: (id: string) => void;
  /** Pop topmost and call its close fn. Returns true if anything was closed. */
  closeTop: () => boolean;
  /** Close every modal. Used on Esc-from-everywhere fallback. */
  closeAll: () => void;
  /** Snapshot of current ids — useful for debugging. */
  ids: () => string[];
}

/**
 * R232q5az: convenience hook for components.
 *   useModalStackRegister('inventory', open, () => setOpen(false));
 * Handles register on open, unregister on close, cleanup on unmount.
 *
 * NOTE: import lazily inside components to avoid circular deps.
 */
export function useModalStackRegister(id: string, open: boolean, close: CloseFn) {
  useEffect(() => {
    const { registerModal, unregisterModal } = useActiveModals.getState();
    if (open) registerModal(id, close);
    else unregisterModal(id);
    return () => useActiveModals.getState().unregisterModal(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

export const useActiveModals = create<ActiveModalsStore>((set, get) => ({
  stack: [],
  registerModal: (id, close) => {
    const cur = get().stack;
    // If this id is already on the stack, replace its close fn but keep
    // its position (idempotent registration during re-render).
    const existingIdx = cur.findIndex((e) => e.id === id);
    if (existingIdx >= 0) {
      const next = cur.slice();
      next[existingIdx] = { id, close };
      set({ stack: next });
      return;
    }
    // Auto-close every previously-open modal so new one is the only one.
    // M01 fix: opening Inventory after Talents should close Talents.
    const toClose = cur.slice();
    set({ stack: [{ id, close }] });
    // Defer close calls so React re-render doesn't conflict with this update.
    if (toClose.length > 0) {
      queueMicrotask(() => {
        for (const e of toClose) {
          if (e.id === id) continue;
          try { e.close(); } catch {}
        }
      });
    }
  },
  unregisterModal: (id) => {
    set((s) => ({ stack: s.stack.filter((e) => e.id !== id) }));
  },
  closeTop: () => {
    const cur = get().stack;
    if (cur.length === 0) return false;
    const top = cur[cur.length - 1]!;
    set({ stack: cur.slice(0, -1) });
    try { top.close(); } catch {}
    return true;
  },
  closeAll: () => {
    const cur = get().stack;
    set({ stack: [] });
    for (const e of cur) {
      try { e.close(); } catch {}
    }
  },
  ids: () => get().stack.map((e) => e.id),
}));
